import { ref } from 'vue';
import { isAxiosError } from 'axios';
import {
  GENERATED_RECORD_NOT_FOUND_MESSAGE,
  getGeneratedRecordForm,
  uploadGeneratedRecordPdf,
  type GeneratedRecordStage,
} from '@/entities/GeneratedRecord';
import { extractApiErrorMessage, useCustomToast } from '@/shared/lib';
import { selectRecordPdf } from './record-pdf-file';

export const RECORD_PDF_SENT_TITLE = 'PDF enviado com sucesso.';
export const RECORD_PDF_SEND_FAILED_MESSAGE = 'Não foi possível enviar o PDF. Tente novamente.';
export const RECORD_PDF_SEND_ERROR_TITLE = 'Não foi possível enviar o PDF.';

export type UploadRecordPdfStep = 'file' | 'stage';

interface UploadRecordPdfOptions {
  recordId: () => number;
  hasPdfResponse: () => boolean;
  /** The flow ended and the row changed: the PDF was saved or the record is gone. */
  onDone: () => void;
  /** The row on screen disagrees with the server; the list fetches again and the flow goes on. */
  onStale: () => void;
}

export function useUploadRecordPdf(options: UploadRecordPdfOptions) {
  const { showSuccessToast, showErrorToast } = useCustomToast();

  const file = ref<File | null>(null);
  const errorMessage = ref<string | null>(null);
  const step = ref<UploadRecordPdfStep>('file');
  const stages = ref<GeneratedRecordStage[]>([]);
  const loadingStages = ref(false);
  const submitting = ref(false);

  function choose(files: ArrayLike<File>): void {
    const selection = selectRecordPdf(files);
    if (selection === null) return;
    if ('error' in selection) {
      errorMessage.value = selection.error;
      return;
    }
    file.value = selection.file;
    errorMessage.value = null;
  }

  function removeFile(): void {
    file.value = null;
    errorMessage.value = null;
  }

  function reportFailure(error: unknown): void {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const serverMessage = extractApiErrorMessage(error);

    if (status === 404) {
      showErrorToast({
        title: RECORD_PDF_SEND_ERROR_TITLE,
        type: 'error',
        message: GENERATED_RECORD_NOT_FOUND_MESSAGE,
      });
      options.onDone();
      return;
    }

    const isClientRefusal = status !== undefined && status >= 400 && status < 500;
    errorMessage.value =
      isClientRefusal && serverMessage !== undefined ? serverMessage : RECORD_PDF_SEND_FAILED_MESSAGE;
  }

  // The stages come from the record form: the branch stage list route asks for a management
  // permission a person who only fills records does not have.
  async function requestSave(): Promise<void> {
    if (file.value === null || loadingStages.value) return;

    loadingStages.value = true;
    errorMessage.value = null;
    try {
      const form = await getGeneratedRecordForm(options.recordId());
      stages.value = form.stages;
      if ((form.record?.hasPdfResponse ?? false) !== options.hasPdfResponse()) options.onStale();
      step.value = 'stage';
    } catch (error) {
      reportFailure(error);
    } finally {
      loadingStages.value = false;
    }
  }

  function closeStage(): void {
    if (submitting.value) return;
    step.value = 'file';
  }

  async function confirmStage(stageId: number): Promise<void> {
    const chosen = file.value;
    if (chosen === null || submitting.value) return;

    submitting.value = true;
    try {
      await uploadGeneratedRecordPdf(options.recordId(), chosen, stageId);
      showSuccessToast({ title: RECORD_PDF_SENT_TITLE, type: 'success' });
      options.onDone();
    } catch (error) {
      step.value = 'file';
      reportFailure(error);
    } finally {
      submitting.value = false;
    }
  }

  return {
    file,
    errorMessage,
    step,
    stages,
    loadingStages,
    submitting,
    choose,
    removeFile,
    requestSave,
    closeStage,
    confirmStage,
  };
}
